import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

const ACTIONS = ["can_view", "can_create", "can_edit", "can_delete"];
const LABELS  = ["View", "Create", "Edit", "Delete"];

export default function PermissionsAdminList() {
  const [roles, setRoles]         = useState([]);
  const [selectedRole, setRole]   = useState("");
  const [perms, setPerms]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    api.get("/admin/roles")
      .then(r => setRoles(r.data?.roles || []))
      .catch(() => toast.error("Failed to load roles"));
  }, []);

  const loadPerms = async (role_id) => {
    setRole(role_id);
    if (!role_id) return setPerms([]);
    setLoading(true);
    try {
      const res = await api.get(`/admin/roles/${role_id}/permissions`);
      setPerms(res.data?.permissions || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };

  const syncAncestors = (next, rowIdx, action, val) => {
    const row = next[rowIdx];
    if (!row?.parent_slug) return;

    if (val) {
      let cur = row;
      while (cur.parent_slug) {
        const pIdx = next.findIndex((p) => p.slug === cur.parent_slug);
        if (pIdx === -1) break;
        next[pIdx] = { ...next[pIdx], [action]: 1 };
        cur = next[pIdx];
      }
      return;
    }

    let cur = row;
    while (cur.parent_slug) {
      const pIdx = next.findIndex((p) => p.slug === cur.parent_slug);
      if (pIdx === -1) break;
      const parent = next[pIdx];
      const anyChild = next.some(
        (p) => p.parent_slug === parent.slug && !!p[action]
      );
      next[pIdx] = { ...parent, [action]: anyChild ? 1 : 0 };
      cur = next[pIdx];
    }
  };

  const syncAncestorsRow = (next, rowIdx, val) => {
    const row = next[rowIdx];
    if (!row?.parent_slug) return;

    if (val) {
      let cur = row;
      while (cur.parent_slug) {
        const pIdx = next.findIndex((p) => p.slug === cur.parent_slug);
        if (pIdx === -1) break;
        const parent = next[pIdx];
        next[pIdx] = ACTIONS.reduce(
          (acc, a) => (cur[a] ? { ...acc, [a]: 1 } : acc),
          { ...parent }
        );
        cur = next[pIdx];
      }
      return;
    }

    let cur = row;
    while (cur.parent_slug) {
      const pIdx = next.findIndex((p) => p.slug === cur.parent_slug);
      if (pIdx === -1) break;
      const parent = next[pIdx];
      next[pIdx] = ACTIONS.reduce((acc, a) => {
        const anyChild = next.some(
          (p) => p.parent_slug === parent.slug && !!p[a]
        );
        return { ...acc, [a]: anyChild ? 1 : 0 };
      }, { ...parent });
      cur = next[pIdx];
    }
  };

  const toggle = (menu_id, action, val) => {
    setPerms((prev) => {
      const next = prev.map((p) => ({ ...p }));
      const idx = next.findIndex((p) => p.menu_id === menu_id);
      if (idx === -1) return prev;
      next[idx] = { ...next[idx], [action]: val ? 1 : 0 };
      syncAncestors(next, idx, action, val);
      return next;
    });
  };

  const toggleRow = (menu_id, val) => {
    setPerms((prev) => {
      const next = prev.map((p) => ({ ...p }));
      const idx = next.findIndex((p) => p.menu_id === menu_id);
      if (idx === -1) return prev;
      const patch = ACTIONS.reduce(
        (acc, a) => ({ ...acc, [a]: val ? 1 : 0 }),
        { ...next[idx] }
      );
      next[idx] = patch;
      syncAncestorsRow(next, idx, val);
      return next;
    });
  };

  const toggleCol = (action, val) => {
    setPerms(prev => prev.map(p => ({ ...p, [action]: val ? 1 : 0 })));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/roles/${selectedRole}/permissions`, { permissions: perms });
      toast.success("Permissions saved successfully");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Permissions</h1>
          <p className="text-sm text-gray-600 mt-1">Assign menu access permissions to each role</p>
        </div>
        {selectedRole && perms.length > 0 && (
          <button
            className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 self-start"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        )}
      </div>

      <div className="mt-6 border rounded-2xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[280px]">
            <label className="text-sm font-semibold">Select Role</label>
            <select
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
              value={selectedRole}
              onChange={e => loadPerms(e.target.value)}
            >
              <option value="">— Choose a role to manage permissions —</option>
              {roles.map(r => (
                <option key={r.role_id} value={r.role_id}>{r.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedRole && (
        <div className="mt-6 border rounded-2xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">
            {roles.find(r => r.role_id == selectedRole)?.name} — Menu Permissions
          </div>

          {loading ? (
            <div className="p-4 text-sm text-gray-600">Loading permissions...</div>
          ) : perms.length === 0 ? (
            <div className="p-4 text-sm text-gray-600">No menus found.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-t bg-white">
                <tr className="text-left">
                  <th className="px-4 py-3 w-[220px]">Menu</th>
                  {LABELS.map(l => (
                    <th key={l} className="px-4 py-3 text-center">{l}</th>
                  ))}
                  <th className="px-4 py-3 text-center">Quick</th>
                </tr>
                <tr className="border-t bg-gray-50">
                  <td className="px-4 py-2 text-xs text-gray-500 font-semibold">Select all</td>
                  {ACTIONS.map(a => (
                    <td key={a} className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-black cursor-pointer"
                        checked={perms.length > 0 && perms.every(p => p[a])}
                        onChange={e => toggleCol(a, e.target.checked)}
                      />
                    </td>
                  ))}
                  <td></td>
                </tr>
              </thead>

              <tbody className="border-t">
                {perms.map(p => (
                  <tr key={p.menu_id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold" style={{paddingLeft: p.parent_slug ? 32 : 16}}>
                      {p.parent_slug && <span className="text-gray-400 mr-1">↳</span>}
                      <span className={p.parent_slug ? "font-normal" : "font-semibold"}>{p.name}</span>
                    </td>

                    {ACTIONS.map(a => (
                      <td key={a} className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-black cursor-pointer"
                          checked={!!p[a]}
                          onChange={e => toggle(p.menu_id, a, e.target.checked)}
                        />
                      </td>
                    ))}

                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-gray-100"
                          onClick={() => toggleRow(p.menu_id, true)}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-gray-100"
                          onClick={() => toggleRow(p.menu_id, false)}
                        >
                          None
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}